import React, { useEffect, useState } from 'react';
import { Clock, Target, ChevronRight, RefreshCw } from 'lucide-react';
import { slides, talkingPoints } from '../data/webinarContent';
export function PresenterNotesView() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  useEffect(() => {
    console.log('Presenter notes view mounted');
    // Get initial slide from localStorage
    const savedSlide = localStorage.getItem('currentSlide');
    if (savedSlide) {
      const slideNum = parseInt(savedSlide);
      console.log('Presenter notes loading saved slide:', slideNum);
      setCurrentSlide(slideNum);
    }
    // Initialize BroadcastChannel for sync
    const channel = new BroadcastChannel('webinar-sync');
    setIsConnected(true);
    console.log('Presenter notes: BroadcastChannel opened');
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'SLIDE_CHANGE') {
        console.log('Presenter notes received slide change:', event.data.slideIndex);
        setCurrentSlide(event.data.slideIndex);
      }
    };
    channel.addEventListener('message', handleMessage);
    // Poll localStorage as backup sync method
    const pollInterval = setInterval(() => {
      const savedSlide = localStorage.getItem('currentSlide');
      if (savedSlide) {
        const slideNum = parseInt(savedSlide);
        if (slideNum !== currentSlide) {
          console.log('Presenter notes: Syncing from localStorage:', slideNum);
          setCurrentSlide(slideNum);
        }
      }
    }, 500);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      clearInterval(pollInterval);
      console.log('Presenter notes: Cleanup complete');
    };
  }, [currentSlide]);
  const currentTalkingPoints = talkingPoints.find(tp => tp.slideId === slides[currentSlide]?.id);
  const nextSlide = slides[currentSlide + 1];
  const syncNow = () => {
    const savedSlide = localStorage.getItem('currentSlide');
    if (savedSlide) {
      const slideNum = parseInt(savedSlide);
      console.log('Manual sync to slide:', slideNum);
      setCurrentSlide(slideNum);
    }
  };
  return <div className="w-full min-h-screen bg-[#1C1C1C] p-8" data-id="element-2">
      <div className="max-w-6xl mx-auto" data-id="element-3">
        {/* Header */}
        <div className="mb-8 pb-6 border-b border-[#4D4D4D]" data-id="element-4">
          <div className="flex items-center justify-between mb-2" data-id="element-5">
            <div className="flex items-center gap-4" data-id="element-6">
              <h1 className="text-3xl font-bold text-[#0FB07D]" data-id="element-7">
                Presenter Notes
              </h1>
              <div className="flex items-center gap-2" data-id="element-8">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-[#0FB07D]' : 'bg-red-500'}`} data-id="element-9"></div>
                <span className="text-[#4D4D4D] text-sm" data-id="element-10">
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-11">
              <button onClick={syncNow} className="flex items-center gap-2 px-3 py-2 bg-[#4D4D4D] text-white rounded-lg hover:bg-[#017C6D] transition-colors text-sm" title="Manually sync with main window" data-id="element-12">
                <RefreshCw size={14} data-id="element-13" />
                Sync
              </button>
              <div className="text-[#F5F5F5] text-lg" data-id="element-14">
                Slide {currentSlide + 1} of {slides.length}
              </div>
            </div>
          </div>
          <div className="text-[#4D4D4D]" data-id="element-15">{slides[currentSlide]?.title}</div>
        </div>

        <div className="grid grid-cols-3 gap-8" data-id="element-16">
          {/* Main Notes - 2/3 width */}
          <div className="col-span-2 space-y-6" data-id="element-17">
            {currentTalkingPoints ? <>
                {/* Timing */}
                <div className="bg-[#017C6D] rounded-lg p-4 flex items-center gap-3" data-id="element-18">
                  <Clock size={24} className="text-white" data-id="element-19" />
                  <div data-id="element-20">
                    <div className="text-white/70 text-sm" data-id="element-21">Time Allocation</div>
                    <div className="text-white text-xl font-semibold" data-id="element-22">
                      {currentTalkingPoints.timing}
                    </div>
                  </div>
                </div>

                {/* Key Messages */}
                <div className="bg-[#4D4D4D]/20 rounded-lg p-6" data-id="element-23">
                  <div className="flex items-center gap-2 mb-4" data-id="element-24">
                    <Target size={20} className="text-[#0FB07D]" data-id="element-25" />
                    <h3 className="text-[#0FB07D] font-semibold text-lg" data-id="element-26">
                      Key Messages
                    </h3>
                  </div>
                  <ul className="space-y-3" data-id="element-27">
                    {currentTalkingPoints.keyMessages.map((message, index) => <li key={index} className="text-[#F5F5F5] pl-4 border-l-2 border-[#0FB07D]" data-id="element-28">
                        {message}
                      </li>)}
                  </ul>
                </div>

                {/* Script */}
                <div className="bg-[#4D4D4D]/20 rounded-lg p-6" data-id="element-29">
                  <h3 className="text-[#F5F5F5] font-semibold text-lg mb-4" data-id="element-30">
                    Script
                  </h3>
                  <div className="space-y-4" data-id="element-31">
                    {currentTalkingPoints.points.map((point, index) => <p key={index} className="text-[#F5F5F5] leading-relaxed text-lg" data-id="element-32">
                        {point}
                      </p>)}
                  </div>
                </div>
              </> : <div className="bg-[#4D4D4D]/20 rounded-lg p-8 text-center" data-id="element-33">
                <p className="text-[#4D4D4D] text-lg" data-id="element-34">
                  No notes available for this slide
                </p>
              </div>}
          </div>

          {/* Next Slide Preview - 1/3 width */}
          <div className="space-y-4" data-id="element-35">
            <div className="bg-[#4D4D4D]/20 rounded-lg p-4 sticky top-8" data-id="element-36">
              <div className="flex items-center gap-2 mb-4" data-id="element-37">
                <ChevronRight size={20} className="text-[#0FB07D]" data-id="element-38" />
                <h3 className="text-[#F5F5F5] font-semibold" data-id="element-39">Next Slide</h3>
              </div>
              {nextSlide ? <div className="space-y-2" data-id="element-40">
                  <div className="text-[#4D4D4D] text-sm" data-id="element-41">
                    Slide {currentSlide + 2}
                  </div>
                  <div className="text-[#F5F5F5] font-medium" data-id="element-42">
                    {nextSlide.title}
                  </div>
                  <div className="text-[#4D4D4D] text-sm mt-4" data-id="element-43">
                    Type: {nextSlide.type}
                  </div>
                </div> : <div className="text-[#4D4D4D]" data-id="element-44">End of presentation</div>}
            </div>

            {/* Quick Tips */}
            <div className="bg-[#017C6D]/20 rounded-lg p-4 border border-[#017C6D]/30" data-id="element-45">
              <h4 className="text-[#0FB07D] font-semibold mb-3 text-sm" data-id="element-46">
                Quick Tips
              </h4>
              <ul className="space-y-2 text-[#F5F5F5] text-sm" data-id="element-47">
                <li data-id="element-48">• Navigate from main window</li>
                <li data-id="element-49">• Pause for chat responses</li>
                <li data-id="element-50">• Check time allocation</li>
                <li data-id="element-51">• Engage with attendees</li>
                <li data-id="element-52">• Click Sync if out of sync</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
}